import { useEffect } from 'react';
import {
  ICustomProperties,
  IEventTelemetry,
} from '@microsoft/applicationinsights-web';

import { appInsightsManager } from './appInsightsManager';

type DependencyList = readonly unknown[];

/** Tracks the component lifetime from the moment of mounting till the unmount. Returns function allowing the insertion of custom events.
 The duration for the custom event will be measured starting with the invocation of the method. */
export const useAppInsightsComponentsTracking = (
  componentName: string,
  event?: IEventTelemetry,
  customProperties?: ICustomProperties,
  deps?: DependencyList
) => {
  const invoked = Date.now();
  useEffect(() => {
    console.debug(`Started tracking ${componentName}`);
    const startTime = Date.now();
    return () => {
      console.debug(`Finishing tracking ${componentName}`);
      const duration = Date.now() - startTime;
      if (event) {
        appInsightsManager._reactPlugin.trackEvent(event, {
          ...customProperties,
          customComponent: true,
          componentName: componentName,
          duration,
        });
      }
    };
  }, deps ?? []);

  return (event: IEventTelemetry, customProperties?: ICustomProperties) => {
    const duration = Date.now() - invoked;
    appInsightsManager._reactPlugin.trackEvent(event, {
      ...customProperties,
      customComponent: true,
      componentName: componentName,
      duration,
    });
  };
};

/** Tracks the component lifetime from the moment of mounting till the unmount. Event with name 'ComponentUsage' is being logged.*/
export const useAppInsightsComponentLifetimeTracking = (
  componentName: string,
  customProperties?: ICustomProperties,
  deps?: DependencyList,
  startTrackCondition?: () => boolean
) => {
  useEffect(() => {
    if (
      (startTrackCondition && startTrackCondition()) ||
      (!startTrackCondition && true)
    ) {
      console.debug(
        `Started tracking ${componentName} with ${JSON.stringify(customProperties)}`
      );
      const startTime = Date.now();
      return () => {
        console.debug(
          `Finishing tracking ${componentName} with ${JSON.stringify(customProperties)}`
        );
        const duration = Date.now() - startTime;
        appInsightsManager._reactPlugin.trackEvent(
          { name: 'ComponentUsage' },
          {
            ...customProperties,
            customComponent: true,
            componentName: componentName,
            duration,
          }
        );
      };
    }
  }, deps ?? []);
};
